import React, { useState, useEffect } from "react";
import ActionCards from '../components/ActionCards';
import ChatBubble from '../components/ChatBubble';
import QuizModal from '../components/QuizModal';
import apiService from '../services/apiService';
import '../Chat.css';

function Chat() {
  const [messages, setMessages] = useState([
    { sender: "bot", text: "Hello! I'm your AI Assistant. How can I help you today?" }
  ]);
  const [input, setInput] = useState("");
  const [showQuizModal, setShowQuizModal] = useState(false);
  const [quizPrefill, setQuizPrefill] = useState({});
  const [workflow, setWorkflow] = useState({ step: "idle" });
  const [courses, setCourses] = useState([]);
  const [lessons, setLessons] = useState([]);

  // Fetch courses on mount
  useEffect(() => {
    apiService.getCourses().then(data => setCourses(data));
  }, []);

  // Helper to add a message
  const addMessage = (msg) => setMessages(msgs => [...msgs, msg]);

  // Handle action card click
  const handleActionCard = (action) => {
    if (action === 'create_quiz') {
      startQuizWorkflow();
    } else {
      addMessage({ sender: 'bot', text: 'That feature is coming soon!' });
    }
  };

  // Detect text intent for question set creation
  const detectQuizIntent = (text) => {
    const lower = text.toLowerCase();
    return lower.includes('create a quiz') || lower.includes('quiz for') || lower.includes('generate quiz') || lower.includes('create a question set') || lower.includes('question set for') || lower.includes('generate question set');
  };

  // Start quiz workflow
  const startQuizWorkflow = () => {
    addMessage({ sender: 'bot', text: "Let's create a question set! Please select a course:",
      buttons: courses.map(c => ({ label: c.title, value: c.id })), vertical: true });
    setWorkflow({ step: 'awaiting_course' });
  };

  // Handle button click in chat bubbles
  const handleButtonClick = async (value) => {
    if (workflow.step === 'awaiting_course') {
      const selectedCourse = courses.find(c => c.id === value);
      addMessage({ sender: 'user', text: selectedCourse.title });
      // Fetch lessons for this course
      const lessonsData = await apiService.getLessonsByCourse(value);
      setLessons(lessonsData);
      addMessage({ sender: 'bot', text: `Now select a lesson from "${selectedCourse.title}":`,
        buttons: lessonsData.map(l => ({ label: l.title, value: l.id })), vertical: true });
      setWorkflow({ step: 'awaiting_lesson', course: selectedCourse });
    } else if (workflow.step === 'awaiting_lesson') {
      const selectedLesson = lessons.find(l => l.id === value);
      addMessage({ sender: 'user', text: selectedLesson.title });
      // Prefill modal with course, lesson, and criteria='mcq'
      const prefill = {
        course: workflow.course?.id,
        lesson: selectedLesson.id,
        criteria: 'mcq'
      };
      setQuizPrefill(prefill);
      setShowQuizModal(true);
      setWorkflow({ step: 'idle' });
      addMessage({ sender: 'bot', text: 'Launching question set creation form with your selections...' });
    }
  };

  // Handle text input (for number of questions or intent)
  const handleInputSend = () => {
    if (!input.trim()) return;
    addMessage({ sender: 'user', text: input });
    // If idle, detect intent
    if (workflow.step === 'idle' && detectQuizIntent(input)) {
      startQuizWorkflow();
      setInput("");
      return;
    }
    // Otherwise, fallback
    addMessage({ sender: 'bot', text: 'Let me check that for you...' });
    setInput("");
  };

  return (
    <div>
      <h1>AI Assistant</h1>
      <ActionCards onAction={handleActionCard} />
      <div className="chat-container">
        <div className="chat-messages">
          {messages.map((m, i) => (
            <ChatBubble key={i} {...m} onButtonClick={handleButtonClick} />
          ))}
        </div>
        {showQuizModal && (
          <QuizModal
            prefill={quizPrefill}
            onClose={() => setShowQuizModal(false)}
          />
        )}
        <div className="chat-input-container">
          <div className="chat-input-wrapper">
            <input
              type="text"
              className="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type your message..."
              onKeyDown={(e) => e.key === 'Enter' && handleInputSend()}
            />
            <button className="btn btn-primary btn-icon" onClick={handleInputSend}>➤</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Chat;
